<template>
  <page>
    <span slot="page-title">
      2018 Election Forecasting Simulator
      <v-chip
        style="font-size: 0.5em; font-weight: 400; letter-spacing: 0.05em; height: 22px;"
        color="accent" text-color="white">Beta
      </v-chip>
      <div style="font-size: 0.5em; font-weight: 500; text-align: center; color: #757575; letter-spacing: 0.05em;">
        The 2018 Forecasting Simulator is in Beta - Find a bug?
        <a href="https://decisiondeskhq.com/contact-us/" target="_blank">Contact us</a>
      </div>
    </span>

    <expenditures-table
      v-if="fullscreen"
      :expenditures="expenditures"
      :original="originalReset"
      :editableFields="editableFields"
      :scenario="selectedScenario"
      @update-row="updateRow"
      @reset-row="resetRow($event)">
      <template slot=buttons>
        <v-btn outline dark color="primary" style="margin: 0;" @click="finishClick">Back</v-btn>
        <v-btn outline dark color="primary" style="margin: 0;" @click="fetchExpenditures(true)">Reset Table</v-btn>
        <v-btn
          color="primary"
          :dark="Object.keys(selectedScenario).length !== 0"
          :light="Object.keys(selectedScenario).length === 0"
          :disabled="Object.keys(selectedScenario).length === 0"
          @click.native.stop="dialog = true">
          Create Report
        </v-btn>
      </template>
    </expenditures-table>

    <loading v-if="loading" color="#333" :loading="loading" text="Loading"></loading>

    <template v-else-if="!loading && !fullscreen">
      <v-card dense class="mb-3">
        <v-layout row wrap>
          <v-flex xs12 sm12 md6 style="text-align: center; padding: 20px;">
            <div v-if="$auth.authenticated">
              <div style="color: #757575; letter-spacing: 0.05em;">Select one of your saved scenarios to edit, or create a new one below.</div>
              <div style="display: flex; align-items: center; justify-content: center;">
                <v-select
                  :items="savedScenarios"
                  item-text="name"
                  return-object
                  v-model="savedScenario"
                  label="Select Saved Scenario"
                  single-line
                  bottom
                  style="max-width: 300px;"
                ></v-select>
                <v-btn
                  color="primary"
                  :dark="savedScenario !== null"
                  :light="!savedScenario"
                  :disabled="!savedScenario"
                  @click="selectScenario(savedScenario.name, false, false, true)">Edit
                </v-btn>
                <v-btn
                  color="primary"
                  :dark="savedScenario !== null"
                  :light="!savedScenario"
                  :disabled="!savedScenario"
                  @click="viewScenario">View
                </v-btn>
                <v-btn
                  color="primary"
                  :dark="savedScenario !== null"
                  :light="!savedScenario"
                  :disabled="!savedScenario"
                  @click="deleteScenario">Delete
                </v-btn>
              </div>
            </div>
            <div v-else>
              <div style="color: #757575; letter-spacing: 0.05em;">Login so you can save your scenarios for later.</div>
              <v-btn color="primary" outline dark @click="loginDialog = true">Login</v-btn>
            </div>
          </v-flex>
          <v-flex xs12 sm12 md6 style="text-align: center; padding: 20px;">
            <div style="color: #757575; letter-spacing: 0.05em;">Learn about the math behind the 2018 Election Forecasting Simulator.</div>
            <v-dialog v-model="methodology" scrollable max-width="600px" style="z-index: 3000;">
              <v-btn color="primary" outline dark slot="activator">Methodology</v-btn>
              <v-card>
                <v-card-title style="border-bottom: 1px solid #bdbdbd;">
                  <h2 style="margin: 0 auto;">2018 Election Forecasting Simulator Methodology</h2>
                </v-card-title>
                <v-card-text style="height: 500px;">
                  <methodology></methodology>
                </v-card-text>
                <v-divider></v-divider>
                <v-card-actions style="text-align: end;">
                  <v-btn color="primary" flat @click.native="methodology = false">Close</v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>
          </v-flex>
        </v-layout>
      </v-card>

      <v-stepper v-model="step" vertical class="mb-5">
        <v-stepper-step step="1" v-bind:complete="step > 1">
          <div style="display: inlne-block; vertical-align: middle;">
            Select Your Mode
          </div>
          <small>Do you want to modify Republican spending, Democratic spending, or both?</small>
        </v-stepper-step>
        <v-stepper-content step="1">
          <mode></mode>
          <v-btn :disabled="!mode" color="primary" @click.native="step = 2">Continue</v-btn>
        </v-stepper-content>
        <v-stepper-step step="2" v-bind:complete="step > 2">
          Track Your Spendings
          <small>This table will track your current spendings and how it changes the favored and expected seats</small>
        </v-stepper-step>
        <v-stepper-content step="2">
          <div style="width: 100%; overflow-x: auto;">
            <scoreboard-mobile :totalSpend="totalSpend" :maxSpend="maxSpend" :fundsRemaining="fundsRemaining" :favored="favored" :expected="expected" class="mb-3" v-if="mobilePhone"></scoreboard-mobile>
            <scoreboard :totalSpend="totalSpend" :maxSpend="maxSpend" :fundsRemaining="fundsRemaining" :favored="favored" :expected="expected" class="mb-3" v-else></scoreboard>
          </div>
          <v-btn color="primary" @click.native="step = 3">Continue</v-btn>
          <v-btn flat @click.native="step = step - 1">Back</v-btn>
        </v-stepper-content>
        <v-stepper-step step="3" v-bind:complete="step > 3">
          Reallocate Money Between Races
          <small>Click on a cell in an expenditure column to edit. Colored columns
            (<v-icon style="color: rgba(250,49,56,0.1);">view_column</v-icon> <v-icon style="color: rgba(102,171,207, 0.1);">view_column</v-icon>)
            are editable
          </small>
        </v-stepper-step>
        <v-stepper-content step="3">
          <template v-if="Object.keys(session).length > 0">
            <div style="margin: 20px 0 10px 0;">Continue editing your current scenario: </div>
            <v-btn
              style="display: block; margin-left: 0;"
              dark outline small color="grey"
              @click="selectScenario(session.name, false, true)"
              >Continue Editing
            </v-btn>
          </template>
          <div style="margin: 20px 0 10px 0;">
            <template v-if="Object.keys(session).length > 0">Or choose </template>
            <template v-else>Choose </template>one of the following scenarios:
            </div>
          <div style="font-size: 0.8em;">
            <span style="font-weight: 600;">Default: </span>
            <span style="font-color: #666; font-style: italic;">
              This scenario starts you off with the actual funds allocated during the 2018 election for both parties.
            </span>
          </div>
          <v-btn
            style="display: block; margin-left: 0;"
            dark outline small color="grey"
            @click="selectScenario(null, true)"
            >Default
          </v-btn>
          <div v-for="(scenario, index) in fitleredScenarios" :key="index" style="font-size: 0.8em;">
            <span style="font-weight: 600;">{{ scenario.name }}: </span>
            <span style="font-color: #666; font-style: italic;" v-if="scenario.description">
              {{ scenario.description }}
            </span>
            <v-btn
              style="display: block; margin-left: 0;"
              dark outline small color="grey"
              @click="selectScenario(scenario.name, false)"
              >{{ scenario.name }}
            </v-btn>
          </div>
          <div class="mt-4">
            <v-btn
              color="primary"
              :dark="Object.keys(selectedScenario).length !== 0"
              :light="Object.keys(selectedScenario).length === 0"
              :disabled="Object.keys(selectedScenario).length === 0"
              @click.native.stop="dialog = true">
              Create Report
            </v-btn>
            <v-btn flat @click.native="step = step - 1">Back</v-btn>
          </div>
        </v-stepper-content>
      </v-stepper>
    </template>

    <v-dialog v-model="loginDialog" max-width="fit-content">
      <v-card style="padding: 20px;">
        <login-signup @login="loginChange"></login-signup>
        <v-card-actions>
          <v-btn color="red" dark outline @click.native="loginDialog = false" :disabled="loadingShare">Cancel</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="dialog" max-width="420">
      <v-card>
        <v-card-title class="headline">Are you sure want to finish your spending plan?</v-card-title>
        <v-alert color="success" icon="check_circle" value="true" v-if="balanceWarning.includes('Great job!')">
          {{ balanceWarning }}
        </v-alert>
        <v-alert color="warning" icon="priority_high" value="true" v-else>
          {{ balanceWarning }}
        </v-alert>
        <div style="padding: 20px;">
          <h4>Enter a name for your scenario (optional)</h4>
          <v-text-field
            label="Enter name here"
            v-model="scenarioName"
          ></v-text-field>
        </div>
        <v-card-actions style="display: flex !important; justify-content: space-between;">
          <v-btn color="red" dark outline @click.native="dialog = false" :disabled="loadingShare">No</v-btn>
          <v-btn color="green" dark outline @click.native="dialogClose" :loading="loadingShare">Yes</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </page>
</template>

<script>
import cloneDeep from 'lodash.clonedeep'
import { mapGetters, mapActions } from 'vuex'

import { Page, LoginSignup } from '@/components'
import { Loading } from '@/components/common'
import {
  ExpendituresTable,
  Methodology,
  Mode,
  Scoreboard,
  ScoreboardMobile
} from '@/components/common/calculator'
import API from '@/api'
import ExpenditureShare from '@/components/common/calculator/ExpenditureShare'
import calculator from '@/mixins/calculator'

const name = 'expenditures'

const mixins = [
  calculator
]

const computed = {
  ...mapGetters({
    chamber: 'calculator/chamber',
    mode: 'calculator/mode',
    mobilePhone: 'ux/mobilePhone',
    screenWidth: 'ux/screenWidth',
    session: 'calculator/session'
  }),
  editableFields () {
    return {
      gop: ['gopExpenditures'],
      dem: ['demExpenditures'],
      god: ['gopExpenditures', 'demExpenditures']
    }[this.mode]
  },
  balanceWarning () {
    const over = 'Looks like you have gone over budget! You can still share your plan, but it won\'t be a realistic simulation.'
    const under = 'Looks like you are under budget! You can still share your plan, but you\'ll be leaving money on the table!'
    const good = 'Great job! Click Yes to share'

    if (this.mode === 'god') {
      if (this.fundsRemaining.dem > 0 || this.fundsRemaining.gop > 0) {
        return under
      } else if (this.fundsRemaining.dem < 0 || this.fundsRemaining.gop < 0) {
        return over
      } else {
        return good
      }
    } else {
      let prop = null
      if (this.mode === 'gop') {
        prop = 'gop'
      } else {
        prop = 'dem'
      }

      if (this.fundsRemaining[prop] > 0) {
        return under
      } else if (this.fundsRemaining[prop] < 0) {
        return over
      } else {
        return good
      }
    }
  },
  filteredExpenditures () {
    if (this.chamber) {
      return this.default.filter(item => item.chamber === this.chamber)
    }
  },
  fitleredScenarios () {
    if (this.mode && this.chamber) {
      return this.scenarios.filter(scenario => {
        return this.mode === scenario.mode && this.chamber === scenario.chamber
      })
    }
  }
}

const methods = {
  ...mapActions({
    setMode: 'calculator/setMode',
    saveSession: 'calculator/saveSession'
  }),
  calculate (expenditures) {
    return expenditures.map(item => this.calculateItem(item))
  },
  resetRow (race) {
    const index = this.expenditures.findIndex(el => el.race === race)
    const original = this.originalReset.find(item => item.race === race)
    this.$set(this.expenditures, index, original)
  },
  fillDefaultScenarios (exps) {
    const expenditures = [...exps]
    const existingRaces = expenditures.map(r => r.race)
    this.filteredExpenditures.forEach((row) => {
      if (existingRaces.includes(row.race) === false) {
        expenditures.push(row)
      }
    })
    return expenditures
  },
  selectScenario (name, isDefault, currentSession, savedSession) {
    this.selectedScenario = { name, isDefault }
    if (isDefault) {
      this.selectedScenario.name = 'Actual Spendings Scenario'
      this.original = this.filteredExpenditures
      this.expenditures = this.calculate(this.filteredExpenditures)
    } else if (currentSession) {
      const expenditures = this.fillDefaultScenarios(this.session.expenditures)
      this.original = expenditures
      this.expenditures = this.calculate(expenditures)
    } else if (savedSession) {
      const expenditures = this.fillDefaultScenarios(this.savedScenario.expenditures)
      this.original = expenditures
      this.expenditures = this.calculate(expenditures)
      this.setMode(this.savedScenario.mode)
    } else {
      const scenario = this.scenarios.find(s => s.name === name && s.chamber === this.chamber && s.mode === this.mode)
      const expenditures = this.fillDefaultScenarios(scenario.expenditures)
      this.original = expenditures
      this.expenditures = this.calculate(expenditures)
    }
    this.originalReset = cloneDeep(this.expenditures)
    this.fullscreen = true
  },
  deleteScenario () {
    this.loading = true
    const index = this.savedScenarios.findIndex(item => item.reportId === this.savedScenario.reportId)
    this.savedScenarios.splice(index, 1)
    API.delete(`scenarios/${this.savedScenario.reportId}`).then(() => {
      this.savedScenario = null
      this.loading = false
    })
  },
  updateRow ({ field, race, value }) {
    const index = this.expenditures.findIndex(exp => exp.race === race)
    this.$set(this.expenditures, index, this.calculateItem({
      ...this.expenditures[index],
      [field]: value
    }))
  },
  dialogClose () {
    const label = {
      gop: 'GOP',
      dem: 'Democratic',
      god: 'GOP and Democratic'
    }[this.mode]
    const name = `How I would have reshuffled ${label} ${this.chamber.charAt(0).toUpperCase() + this.chamber.slice(1)} outside money in 2018`
    const data = {
      name: this.scenarioName ? this.scenarioName : name,
      mode: this.mode,
      chamber: this.chamber,
      expenditures: this.expenditures.filter(item => item.gopChange.value !== 0 || item.demChange.value !== 0),
      email: this.$auth.authenticated ? this.$auth.profile.email : undefined
    }
    this.loadingShare = true
    API.post('scenarios/', data).then((response) => {
      if (response.status === 201) {
        const scenario = response.data
        this.dialog = false
        this.$router.push({
          name: 'scenario',
          params: {
            chamber: this.chamber,
            default: this.filteredExpenditures,
            expenditures: this.expenditures,
            scenario
          },
          query: {
            id: scenario.reportId
          }
        })
      } else {
        // error
      }
      this.loadingShare = false
    })
  },
  viewScenario () {
    this.$router.push({
      name: 'scenario',
      query: {
        id: this.savedScenario.reportId
      }
    })
  },
  fetchExpenditures (reset) {
    const promises = []

    promises.push(
      API.get('expenditures/').then((data) => {
        this.default = data
      })
    )
    promises.push(
      API.get('scenarios/').then((data) => {
        this.scenarios = data
      })
    )

    if (this.$auth.authenticated) {
      const params = {
        email: this.$auth.profile.email
      }
      promises.push(
        API.get('scenarios/', { params }).then((data) => {
          this.savedScenarios = data
        })
      )
    }

    this.loading = true
    Promise.all(promises).then(() => {
      this.loading = false
    })

    if (reset) {
      this.selectScenario(this.selectedScenario.name, this.selectedScenario.isDefault)
    }
  },
  finishClick () {
    this.saveSession({
      name: this.selectedScenario.name,
      expenditures: this.expenditures
    })
    this.fullscreen = false
  },
  loginChange () {
    setTimeout(() => {
      this.loginDialog = false
      this.fetchExpenditures()
    }, 1000)
  },
  toggleZIndex () {
    const footer = document.getElementById('footer')
    const header = document.getElementById('header')
    if (this.dialog || this.methodology || this.loginDialog) {
      footer.style.zIndex = 200
      header.style.zIndex = 200
    } else {
      footer.style.zIndex = 550
      header.style.zIndex = 550
    }
  }
}

const watch = {
  dialog () {
    this.toggleZIndex()
  },
  methodology () {
    this.toggleZIndex()
  },
  loginDialog () {
    this.toggleZIndex()
  }
}

const components = {
  ExpendituresTable,
  Loading,
  LoginSignup,
  Methodology,
  Mode,
  Page,
  Scoreboard,
  ScoreboardMobile,
  ExpenditureShare
}

export default {
  name,
  mixins,
  computed,
  methods,
  watch,
  components,
  metaInfo: {
    title: 'Forecasting Spending Simulator',
    meta: [
      {
        vmid: 'og:title',
        name: 'og:title',
        content: 'Forecasting Spending Simulator'
      }, {
        vmid: 'twitter:title',
        name: 'twitter:title',
        content: 'Forecasting Spending Simulator'
      }, {
        vmid: 'description',
        name: 'description',
        content: '2018 U.S. midterm forecasting simulator for Senate and House Congressional elections. Modify outside spending totals for Republicans, Democrats, or both parties to see how the outcome of each race would change if money were allocated in different scenarios.'
      }, {
        vmid: 'og:description',
        name: 'og:description',
        content: '2018 U.S. midterm forecasting simulator for Senate and House Congressional elections. Modify outside spending totals for Republicans, Democrats, or both parties to see how the outcome of each race would change if money were allocated in different scenarios.'
      }, {
        vmid: 'twitter:description',
        name: 'twitter:description',
        content: '2018 U.S. midterm forecasting simulator for Senate and House Congressional elections. Modify outside spending totals for Republicans, Democrats, or both parties to see how the outcome of each race would change if money were allocated in different scenarios.'
      }, {
        vmid: 'og:image',
        name: 'og:image',
        content: 'https://s3.us-east-2.amazonaws.com/forecasting.decisiondeskhq.com/spending-simulator-share.png'
      }, {
        vmid: 'og:image:secure_url',
        name: 'og:image:secure_url',
        content: 'https://s3.us-east-2.amazonaws.com/forecasting.decisiondeskhq.com/spending-simulator-share.png'
      }, {
        vmid: 'twitter:image',
        name: 'twitter:image',
        content: 'https://s3.us-east-2.amazonaws.com/forecasting.decisiondeskhq.com/spending-simulator-share.png'
      }
    ]
  },
  data () {
    return {
      step: 1,
      fullscreen: false,
      loading: false,
      expenditures: [],
      original: [],
      originalReset: [],
      default: [],
      scenarios: [],
      dialog: false,
      loginDialog: false,
      shareUrl: '',
      loadingShare: false,
      selectedScenario: {},
      methodology: false,
      savedScenarios: [],
      savedScenario: null,
      scenarioName: null
    }
  },
  beforeMount () {
    this.fetchExpenditures()
  }
}
</script>

<style>
.scroll-info {
  display: flex;
  align-items: center;
  font-size: 0.8em;
  color: #bdbdbd;
  letter-spacing: 0.03em;
}
</style>



// WEBPACK FOOTER //
// src/pages/Expenditures.vue